import { Accordion, Box, Center, Flex, Text } from '@chakra-ui/react';
import { CourseKind } from '@duke-hq/svc-api-db';
import React, { useMemo } from 'react';
import { getStructuredUnitOfCompetencies } from '@duke-hq/svc-api/lib/enrolment/getStructuredUnitOfCompetencies';
import {
  defaultLabels,
  type CourseEnrolmentComponentProps,
  type ScheduleAdjustment,
} from './types';
import { SemesterGroupHeader } from './SemesterGroupHeader';
import { UnitScheduleItem } from './UnitScheduleItem';
import { pxToRem } from '@duke-hq/ui/constants/scalable';

function CourseItemsStatusAccordion({
  courseEnrolment,
  assessmentsInRevision,
  UnitActionsComponent,
  labels,
  getAssessmentLink,
}: CourseEnrolmentComponentProps): React.ReactElement {
  const l = { ...defaultLabels, ...labels };
  const { courseEnrolmentScheduleItems, unitsOfCompetencyById } = courseEnrolment;

  const processedUnits = useMemo(
    () =>
      getStructuredUnitOfCompetencies(
        courseEnrolmentScheduleItems,
        unitsOfCompetencyById,
        new Set<string>(assessmentsInRevision?.map((item) => item.unitOfCompetency.id)),
        courseEnrolment,
        courseEnrolment.completedWorkPlacementUocIds,
      ),
    [courseEnrolment, unitsOfCompetencyById, courseEnrolmentScheduleItems, assessmentsInRevision],
  );

  const scheduleAdjustmentsByItemId = useMemo(() => {
    const map = new Map<string, ScheduleAdjustment>();
    for (const adjustment of courseEnrolment.CourseEnrolmentScheduleAdjustments) {
      map.set(adjustment.item_id, adjustment);
    }
    return map;
  }, [courseEnrolment.CourseEnrolmentScheduleAdjustments]);

  const isSkillSet = useMemo(
    () => courseEnrolment.course?.kind === CourseKind.SKILL_SET,
    [courseEnrolment.course?.kind],
  );

  return (
    <Flex
      fontSize="sm"
      direction="column"
      w="full"
      borderTop="1px solid"
      borderColor="border.default"
    >
      <Flex justifyContent="space-between">
        <Flex>
          <Box w="9"></Box>
          <Box p={2}>{l.unit}</Box>
        </Flex>
        <Flex>
          <Text p={2} w={pxToRem(120)} textAlign="center">
            {l.releaseDate}
          </Text>
          <Box p={2} w={pxToRem(136)}>
            <Center>{l.status}</Center>
          </Box>
          {UnitActionsComponent ? <Box w="14"></Box> : null}
        </Flex>
      </Flex>

      <Accordion w="full" allowToggle>
        {Object.entries(processedUnits).map(([semester, units]) => (
          <React.Fragment key={semester}>
            <SemesterGroupHeader
              semester={semester}
              units={units}
              UnitActionsComponent={UnitActionsComponent}
              isSkillSet={isSkillSet}
              l={l}
            />
            {units.map((unit) => (
              <UnitScheduleItem
                unitSchedule={unit}
                key={unit.id}
                courseEnrolmentId={courseEnrolment.id}
                courseEnrolmentScheduleItems={courseEnrolmentScheduleItems}
                UnitActionsComponent={UnitActionsComponent}
                enrolmentStatus={courseEnrolment.status}
                isAccredited={courseEnrolment.course?.accredited ?? false}
                isWorkPlacement={Boolean(
                  unitsOfCompetencyById[unit.unitOfCompetencyId]?.work_placement,
                )}
                isLLN={semester === '0'}
                scheduleAdjustment={
                  scheduleAdjustmentsByItemId.get(unit.unitOfCompetencyId) ?? null
                }
                getAssessmentLink={getAssessmentLink}
              />
            ))}
          </React.Fragment>
        ))}
      </Accordion>
    </Flex>
  );
}

export default CourseItemsStatusAccordion;
